/**
 * StartReviewForm - Full-page BaseForm for starting a review session.
 *
 * Replaces the old StartReviewModal with a full-page form aligned to
 * MatterForm and CreateTableForm patterns. Uses BaseForm for consistent
 * validation and layout.
 *
 * URL: /reviews?add=true[&playbook=<uuid>]
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { Collaborator } from '../shared/CollaboratorSelector';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import Badge from '../shared/TailwindComponents/Badge';
import FieldShell, { FOCUS_RING, SELECTED_TINT } from '../shared/TailwindComponents/FieldShell';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { startReview } from '../../store/actions/Review/ReviewActions';
import { fetchMatterDropdowns } from '../../store/actions/Matters/MatterActions';
import { fetchDataSources } from '../../store/actions/AIStudioAction';
import { PlaybookListItem } from '../../types/playbook';
import { showModal } from '../../store/actions/modalActions';
import { PiShieldCheckLight } from 'react-icons/pi';
import apis from '../../utils/apis';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import useAIPreferences from '../../hooks/useAIPreferences';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, startReviewFormTourSteps } from '../shared/FeatureTour';

// ─── Custom field: Playbook selector ─────────────────────────────────────────

interface PlaybookSelectorProps {
  value: string | null;
  onChange: (value: string) => void;
  playbooks: PlaybookListItem[];
  isLoading: boolean;
  error?: string;
  t: (key: string) => string;
}

const PlaybookSelector: React.FC<PlaybookSelectorProps> = ({
  value,
  onChange,
  playbooks,
  isLoading,
  error,
  t,
}) => {
  const dispatch = useAppDispatch();
  // The picker fetches its own list, so remember what came back from it - that
  // way the trigger can name a playbook even if this form's list is stale.
  const [picked, setPicked] = useState<PlaybookListItem | null>(null);
  const selected =
    (value && (playbooks.find((pb) => pb.uuid === value) || (picked?.uuid === value ? picked : null))) || null;

  // Shared picker via GlobalModal - same modal the create-table form opens.
  const openPicker = () => {
    dispatch(
      showModal({
        showModal: true,
        type: 'playbookPicker',
        message: '',
        selectedPlaybookUuid: value || '',
        onPlaybookSelect: (pb) => {
          setPicked(pb);
          onChange(pb?.uuid || '');
        },
      })
    );
  };

  if (isLoading) {
    return <div className="py-2 text-sm text-fg-muted">{t('Loading playbooks...')}</div>;
  }

  return (
    <FieldShell error={error} translate={false}>
      <button
        type="button"
        onClick={openPicker}
        className={clsx(
          'group flex w-full items-center justify-between rounded-md border px-4 py-2.5 text-start',
          'transition-colors duration-200 ease-slide',
          FOCUS_RING,
          selected
            ? SELECTED_TINT
            : error
              ? 'border-danger-500 bg-surface-sunken/70 ring-1 ring-danger-500/30 hover:bg-surface-sunken'
              : 'border-hairline bg-surface-sunken/70 hover:bg-surface-sunken'
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <PiShieldCheckLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
          {selected ? (
            <div className="min-w-0">
              <span className="block truncate text-sm font-medium text-fg-strong">
                {selected.name}
              </span>
              <span className="text-xs text-fg-muted">
                {selected.clause_count} {t('clauses')} · {selected.global_rule_count} {t('rules')}
                {selected.agreement_types?.length > 0 &&
                  ` · ${selected.agreement_types.slice(0, 2).join(', ')}${selected.agreement_types.length > 2 ? ` +${selected.agreement_types.length - 2}` : ''}`}
              </span>
            </div>
          ) : (
            <span className="text-sm text-fg-muted">{t('Browse playbooks...')}</span>
          )}
        </div>
        <span className="flex-shrink-0 text-xs text-fg-muted opacity-0 transition-opacity group-hover:opacity-100">
          {selected ? t('Change') : t('Browse')}
        </span>
      </button>
    </FieldShell>
  );
};

// ─── Custom field: Data Source selector ───────────────────────────────────────

interface DataSourceSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  dataSources: Array<{ uuid: string; name: string; description?: string; status?: string; document_count?: number }>;
  isLoading: boolean;
  t: (key: string) => string;
}

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  value,
  onChange,
  dataSources,
  isLoading,
  t,
}) => {
  const safeValue = value || [];

  const toggle = (uuid: string) => {
    onChange(
      safeValue.includes(uuid) ? safeValue.filter((id) => id !== uuid) : [...safeValue, uuid],
    );
  };

  if (isLoading) {
    return <div className="py-2 text-sm text-fg-muted">{t('Loading data sources...')}</div>;
  }

  if (dataSources.length === 0) {
    return (
      <div className="py-2 text-sm text-fg-muted">
        {t('No data sources available. You can create them in AI Studio.')}
      </div>
    );
  }

  return (
    <div className="max-h-36 space-y-1 overflow-y-auto">
      {dataSources.map((ds) => (
        <button
          key={ds.uuid}
          type="button"
          onClick={() => toggle(ds.uuid)}
          className={clsx(
            'w-full rounded-md border px-3 py-2 text-start text-sm',
            'transition-colors duration-200 ease-slide',
            FOCUS_RING,
            safeValue.includes(ds.uuid)
              ? SELECTED_TINT
              : 'border-hairline bg-surface-sunken/70 text-fg-body hover:bg-surface-sunken'
          )}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">{ds.name}</span>
            <div className="flex items-center gap-1.5">
              {ds.document_count !== undefined && (
                <Badge color="gray">{ds.document_count} docs</Badge>
              )}
              {ds.status && (
                <Badge color={ds.status === 'ready' ? 'green' : 'gray'}>
                  {ds.status}
                </Badge>
              )}
            </div>
          </div>
          {ds.description && (
            <p className="mt-0.5 line-clamp-1 text-xs text-fg-muted">{ds.description}</p>
          )}
        </button>
      ))}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const StartReviewForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();

  // Pre-select playbook from URL param (e.g. /reviews?add=true&playbook=<uuid>)
  const preselectedPlaybook = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('playbook') || '';
  }, [location.search]);

  const isUploading = useSelector((s: RootState) => (s as any).review?.isUploading ?? false);

  // Data sources from Redux (populated by fetchDataSources dispatch)
  const reduxDataSources = useSelector((s: RootState) => (s as any).aistudio?.indexes || []);
  const reduxDsLoading = useSelector((s: RootState) => (s as any).aistudio?.loading ?? false);

  // Filter to only ready/indexed data sources
  const readyDataSources = useMemo(
    () =>
      reduxDataSources.filter((ds: any) => {
        const idxStatus = (ds.indexer_status || '').toLowerCase();
        const dsStatus = (ds.status || '').toLowerCase();
        return (
          ['success', 'completed'].includes(idxStatus) ||
          ['ready', 'active'].includes(dsStatus)
        );
      }),
    [reduxDataSources],
  );

  // Data state
  const [playbooks, setPlaybooks] = useState<PlaybookListItem[]>([]);
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(true);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<DropdownOption[]>([]);
  const [languageOptions, setLanguageOptions] = useState<DropdownOption[]>([]);
  /**
   * Straight from the store - the bootstrap hydrated it, so the output-language
   * select is preselected on the first render instead of after this screen's
   * own dropdown fetch comes back.
   */
  const aiPreferences = useAIPreferences();
  const defaultLanguage = aiPreferences.language;
  // Kept for the jurisdiction field that is commented out below - sourced from
  // the store like everything else, so it is correct if that field returns.
  const defaultJurisdiction = aiPreferences.jurisdiction;

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'start-review-form',
    steps: startReviewFormTourSteps,
    readyToShow: !isLoadingPlaybooks,
    autoStartDelay: 600,
  });

  // ─── Fetch dropdown data ────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      // Fetch playbooks
      setIsLoadingPlaybooks(true);
      try {
        const response = await apis.get('/playbooks', { params: { page: 1, page_size: 100 } });
        if (response.status === 200) {
          setPlaybooks(response.data.items || []);
        }
      } catch {
        // Silent - user sees empty list
      } finally {
        setIsLoadingPlaybooks(false);
      }

      // Fetch jurisdictions and languages (with AI settings defaults)
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns() as any);
        if (dropdownsData?.jurisdictions) {
          setJurisdictionOptions(dropdownsData.jurisdictions);
        }
        if (dropdownsData?.languages) {
          setLanguageOptions(dropdownsData.languages);
        }
        // Option lists only - the selected defaults come from the store.
      } catch {
        // Silent - the option lists stay empty; defaults are unaffected
      }

      // Fetch data sources → stored in Redux (state.aistudio.indexes)
      dispatch(fetchDataSources({ page: 1, limit: 100 }) as any);
    };

    loadData();
  }, [dispatch]);

  // ─── Form schema ───────────────────────────────────────────────────

  const schema = useMemo(
    () =>
      new FormSchema('startReview', [
        new FormField('name', 'text', false, t('Review Name'), {
          customValidator: (value: any) => {
            if (value && typeof value === 'string' && value.trim().length > 200) {
              return { isValid: false, error: t('Name must be 200 characters or fewer') };
            }
            return { isValid: true, sanitized: value };
          },
        }),
        new FormField('playbook', 'text', true, t('Playbook'), {
          customValidator: (value: any) => {
            if (!value || (typeof value === 'string' && !value.trim())) {
              return { isValid: false, error: t('Select a playbook to review against') };
            }
            return { isValid: true, sanitized: value };
          },
        }),
        new FormField('documents', 'text', true, t('Documents'), {
          customValidator: (value: any) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              return { isValid: false, error: t('Upload at least one document to review') };
            }
            return { isValid: true, sanitized: value };
          },
        }),
        // new FormField('jurisdiction', 'text', false, t('Jurisdiction'), {
        //   customValidator: () => ({ isValid: true }),
        // }),
        new FormField('language', 'text', false, t('Language'), {
          customValidator: () => ({ isValid: true }),
        }),
        // new FormField('data_sources', 'text', false, t('Data Sources'), {
        //   customValidator: () => ({ isValid: true }),
        // }),
        new FormField('collaborators', 'text', false, t('Access'), {
          customValidator: () => ({ isValid: true }),
        }),
      ]),
    [t],
  );

  // ─── Form sections ─────────────────────────────────────────────────

  const sections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        title: t('Review Details'),
        description: t('Name your review session and set the output language.'),
        dataTourId: 'start-review-name',
        gridCols: 2,
        fields: [
          {
            name: 'name',
            type: 'input',
            label: t('Review Name'),
            placeholder: t('e.g. Q4 Vendor Agreement Review'),
            required: false,
            gridSize: 'half',
          } as BaseFormFieldConfig,
          {
            name: 'language',
            type: 'dropdown',
            label: t('AI Output Language'),
            options: languageOptions,
            placeholder: t('Select language'),
            required: false,
            gridSize: 'half',
            customProps: { fullWidth: true },
            description: t('Language for AI-generated findings, suggestions, and redlines.'),
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Access'),
        description: t('Grant access to people who should collaborate on this review.'),
        dataTourId: 'start-review-collaborators',
        fields: [
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            // No field-level title - the section header ("Access") already labels
            // this, so we don't want the selector to repeat it.
            label: '',
            required: false,
            gridSize: 'full',
            customProps: {
              allowedPermissions: ['edit', 'view'],
              showOwnerSection: true,
              ownerLabel: t('You are the review owner (admin)'),
            },
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Select Playbook'),
        description: t('Choose a playbook with the clause rules and global rules to review against.'),
        dataTourId: 'start-review-playbook',
        fields: [
          {
            name: 'playbook',
            type: 'custom',
            label: t('Playbook'),
            required: true,
            gridSize: 'full',
            customComponent: PlaybookSelector,
            customProps: { playbooks, isLoading: isLoadingPlaybooks, t },
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Documents to Review'),
        description: t('Upload the contracts or documents you want to review. Each document will be analyzed against the playbook rules.'),
        dataTourId: 'start-review-documents',
        fields: [
          {
            name: 'documents',
            type: 'fileUploader',
            label: t('Documents'),
            required: true,
            gridSize: 'full',
            // No file cap - reviews accept any number of documents, handled the
            // same way tables are (per-document queued processing).
            allowedExtensions: ['pdf', 'docx', 'doc', 'txt'],
          } as BaseFormFieldConfig,
        ],
      },
    ],
    [t, playbooks, isLoadingPlaybooks, languageOptions],
  );

  // ─── Submit ─────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      if (!checkAiFeatureGate('review_ai')) return;
      const playbookUuid = formData.playbook;
      const files = formData.documents;
      // const jurisdiction = formData.jurisdiction;
      // const dataSourceIds = formData.data_sources;
      const reviewName = formData.name;
      const language = formData.language;
      const collaborators: Collaborator[] = formData.collaborators || [];

      if (!playbookUuid || !files?.length) return;

      const result = await dispatch(
        startReview(playbookUuid, {
          files: Array.isArray(files) ? files : [files],
          name: reviewName?.trim() || undefined,
          // jurisdiction: Array.isArray(jurisdiction) && jurisdiction.length > 0 ? jurisdiction : undefined,
          // data_source_ids: Array.isArray(dataSourceIds) && dataSourceIds.length > 0 ? dataSourceIds : undefined,
          language: language || undefined,
          collaborators: collaborators.length > 0 ? collaborators : undefined,
        }) as any,
      );

      const reviewUuid = result?.session_uuid || result?.review_uuid;
      if (reviewUuid) {
        history.push(`/reviews/${reviewUuid}`);
      }
    },
    [dispatch, history],
  );

  const handleCancel = useCallback(() => {
    history.push('/reviews');
  }, [history]);

  // ─── Breadcrumbs ────────────────────────────────────────────────────

  const breadcrumbs = useMemo(
    () => [
      { name: t('Reviews'), href: '/reviews', current: false },
      { name: t('New Review'), href: '/reviews?add=true', current: true },
    ],
    [t],
  );

  if (aiPreferences.isHydrating) {
    return <Skeleton variant="form" rows={5} />;
  }

  return (
    <>
      <BaseForm
        title={t('Start Contract Review')}
        description={t(
          'Select a playbook and upload documents to review against your firm standards, clause rules, and global rules.',
        )}
        sections={sections}
        schema={schema}
        initialValues={{
          name: '',
          playbook: preselectedPlaybook,
          documents: [],
          language: defaultLanguage || '',
          // jurisdiction: defaultJurisdiction ? [defaultJurisdiction] : [],
          // data_sources: [],
          collaborators: [],
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel={isUploading ? t('Starting...') : t('Start Review')}
        cancelLabel={t('Cancel')}
        isSubmitting={isUploading}
        submitDisabled={!hasAiFeatureAccess('review_ai')}
        submitTooltip={t('Upgrade your plan to use AI Review.')}
        maxWidth="max-w-3xl"
      />

      <FeatureTour
        steps={startReviewFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </>
  );
};

export default StartReviewForm;
